import csmKeys from "assets/text/csmKeys";
import { text } from "components/common/Text";
import InstFinder from "containers/@Institution/@Roster/Finder";
import React, { lazy } from "react";
import { Navigate } from "react-router";

import ManageCommittees from "components/Administration/ManageCommittees";

import { ADMIN_ROUTES, INSTITUTION_ROUTES, PERSON_ROUTES } from "./pathnames";

const Credentialing = lazy(() => import("../containers/@Credentialing"));
const Home = lazy(() => import("../containers/@Person/PersonRoster/MyTaskContainer"));
const PersonRegistrationData = lazy(() => import("../containers/@Person/PersonRoster/PersonRegistrationData"));
const AffiliationsContainer = lazy(() => import("../containers/@Person/PersonRoster/AffiiationsContainer"));
const LmsTrainingContainer = lazy(() => import("../containers/@Person/PersonRoster/LmsTrainingContainer"));
const PersonAttributesContainer = lazy(() => import("../containers/@Person/PersonRoster/PersonAttributesContainer"));
const PersonRosterRolesContainer = lazy(() => import("../containers/@Person/PersonRoster/PersonRolesContainer"));
const PersonContactInfoContainer = lazy(() => import("../containers/@Person/PersonRoster/PersonContactInfoContainer"));
const Finder = lazy(() => import("../containers/@Person/PersonRoster/Finder/index"));
const PersonRolesBrowser = lazy(() => import("../containers/@Person/RolesContainer"));
const PersonBrowser = lazy(() => import("../containers/@Person/@Browser"));
const MyNMCBrowser = lazy(() => import("../containers/@Institution/@Roster/MyNMCBrowserContainer"));
// lazy loading institution routes
const InstitutionRostersContainer = lazy(() =>
  import("../containers/@Institution/@Roster/InstitutionRostersContainer")
);
const AddressesContactsContainer = lazy(() => import("../containers/@Institution/@Roster/AddressesContactsContainer"));
const InstitutionProvidersContainer = lazy(() =>
  import("../containers/@Institution/@Roster/InstitutionProvidersContainer")
);
const SiteRestrictionsContainer = lazy(() => import("../containers/@Institution/@Roster/SiteRestrictionsContainer"));
const CopyPersonContainer = lazy(() => import("../containers/@Institution/@Roster/CopyPersonContainer"));

const InstitutionCredentialsContainer = lazy(() =>
  import("../containers/@Institution/@Roster/InstitutionCredentialsContainer")
);
const InstitutionAttributesContainer = lazy(() =>
  import("../containers/@Institution/@Roster/InstitutionAttributesContainer")
);

const InstitutionBrowser = lazy(() => import("../containers/@Institution/@Browser"));
const FwaBrowserContainer = lazy(() => import("../containers/@Institution/@Roster/FwaContainer"));
const RUMS = lazy(() => import("../components/Administration/RUMS"));
const ApprovalMappings = lazy(() => import("../components/Administration/ApprovalMappings"));
const PersonRoleSetup = lazy(() => import("../components/Administration/PersonRoleSetup"));
const AttributeSetup = lazy(() => import("../components/Administration/AttributeSetup"));
const NCILapsGrant = lazy(() => import("../components/Administration/NCILAPSGrant"));
const UnregisteredInstitution = lazy(() => import("../components/Administration/UnregisteredInstitution"));

const appRoutes = [
  {
    path: "/",
    name: text("routes.my_task"),
    isExact: true,
    showInMenu: true,
    isNewTab: false,
    element: <Home />,
    subRoutes: [],
    permission: csmKeys.ROSTERING_WELCOME_VIEW,
    helpContext: "home"
  },

  {
    path: "/credentialing",
    name: text("routes.credentialing"),
    isExact: true,
    showInMenu: true,
    isNewTab: false,
    element: <Credentialing />,
    subRoutes: [],
    permission: csmKeys.ROSTERING_CREDENTIALING,
    helpContext: "credentialing"
  },

  {
    path: INSTITUTION_ROUTES.ROOT,
    name: text("routes.institution.institution"),
    isExact: true,
    showInMenu: true,
    isNewTab: false,
    element: <Navigate to={INSTITUTION_ROUTES.AFFILIATION} replace={true} />,
    permission: csmKeys.ROSTERING_INSTITUTION_VIEW,
    helpContext: INSTITUTION_ROUTES.AFFILIATION,
    subRoutes: [
      {
        path: INSTITUTION_ROUTES.AFFILIATION,
        name: text("routes.institution.rosters"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <InstitutionRostersContainer />,
        permission: csmKeys.ROSTERING_INSTITUTION_VIEW,
        helpContext: "institution/affiliation"
      },
      {
        path: INSTITUTION_ROUTES.ADDRESSES_CONTACTS,
        name: text("routes.institution.address_contacts"),
        isExact: true,
        showInMenu: false,
        element: <AddressesContactsContainer />,
        permission: csmKeys.ROSTERING_INSTITUTION_VIEW,
        helpContext: "institution/addreessesContacts"
      },
      {
        path: INSTITUTION_ROUTES.SITE_RESTRICTIONS,
        name: text("routes.institution.site_restriction"),
        isExact: true,
        showInMenu: false,
        element: <SiteRestrictionsContainer />,
        permission: csmKeys.ROSTERING_SITERESTRICTION_VIEW,
        helpContext: "institution/siteRestrictions"
      },
      {
        path: INSTITUTION_ROUTES.COPY_PERSONS,
        name: text("routes.institution.copy_persons"),
        isExact: true,
        showInMenu: false,
        element: <CopyPersonContainer />,
        permission: csmKeys.ROSTERING_INSTITUTION_PERSON_COPY,
        helpContext: "institution/copyPersons"
      },
      {
        path: INSTITUTION_ROUTES.ATTRIBUTES,
        name: text("routes.institution.attributes"),
        isExact: true,
        showInMenu: false,
        element: <InstitutionAttributesContainer />,
        permission: csmKeys.ROSTERING_INSTITUTION_ATTRIBUTES_VIEW,
        helpContext: "institution/attributes"
      },
      {
        path: INSTITUTION_ROUTES.CREDENTIALS,
        name: text("routes.institution.credentials"),
        isExact: true,
        showInMenu: false,
        element: <InstitutionCredentialsContainer />,
        permission: csmKeys.ROSTERING_PROVIDER_CREDENTIAL,
        helpContext: "institution/providers"
      },
      {
        path: INSTITUTION_ROUTES.PROVIDERS,
        name: text("routes.institution.providers"),
        isExact: true,
        showInMenu: false,
        element: <InstitutionProvidersContainer />,
        permission: csmKeys.ROSTERING_PROVIDER_VIEW,
        helpContext: "institution/providers"
      },
      {
        path: INSTITUTION_ROUTES.BROWSER,
        name: text("routes.institution.browser"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <InstitutionBrowser />,
        permission: csmKeys.ROSTERING_INSTITUTION,
        helpContext: "institution/browser"
      },
      {
        path: INSTITUTION_ROUTES.NMC_BROWSER,
        name: text("routes.institution.my_nmc_browser"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <MyNMCBrowser />,
        permission: csmKeys.ROSTERING_INSTITUTION_VIEW,
        helpContext: "institution/nmcbrowser"
      },
      {
        path: INSTITUTION_ROUTES.FWA_BROWSER,
        name: text("routes.institution.fwa_browser"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <FwaBrowserContainer />,
        permission: csmKeys.ROSTERING_FWA_MAINTENANCE_VIEW,
        helpContext: "institution/fwabrowser"
      },
      {
        path: INSTITUTION_ROUTES.FIND,
        name: text("routes.institution.institution_finder"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <InstFinder />,
        permission: csmKeys.ROSTERING_INSTITUTION,
        helpContext: "find"
      }
    ]
  },
  {
    path: PERSON_ROUTES.ROOT,
    showInMenu: true,
    isExact: true,
    isNewTab: false,
    element: <Navigate to={PERSON_ROUTES.AFFILIATION} replace={true} />,
    name: text("routes.person.person"),
    permission: csmKeys.ROSTERING_PERSON,
    helpContext: "person/affiliation",
    subRoutes: [
      {
        path: PERSON_ROUTES.AFFILIATION,
        name: text("routes.person.roster"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <AffiliationsContainer />,
        permission: csmKeys.ROSTERING_PERSON,
        helpContext: "person/affiliation"
      },
      {
        path: PERSON_ROUTES.R_DATA,
        name: text("routes.person.registration_data"),
        isExact: true,
        element: <PersonRegistrationData />,
        permission: csmKeys.ROSTERING_PERSON_REGISTRATION_DATA_VIEW,
        helpContext: "person/registrationData"
      },
      {
        path: PERSON_ROUTES.ATTR,
        name: text("routes.person.attributes"),
        isExact: true,
        element: <PersonAttributesContainer />,
        permission: csmKeys.ROSTERING_PERSON_ATTRIBUTE_VIEW,
        helpContext: "person/attributes"
      },
      {
        path: PERSON_ROUTES.C_INFO,
        name: text("routes.person.contact_info"),
        isExact: true,
        element: <PersonContactInfoContainer />,
        permission: csmKeys.ROSTERING_PERSON,
        helpContext: "person/contactInfo"
      },
      {
        path: PERSON_ROUTES.LMS,
        name: text("routes.person.lms_training"),
        isExact: true,
        element: <LmsTrainingContainer />,
        permission: csmKeys.ROSTERING_PERSON_LMS_TRAINING_VIEW,
        helpContext: "person/LMStraining"
      },
      {
        path: PERSON_ROUTES.BROWSER,
        name: text("routes.person.browser"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <PersonBrowser />,
        permission: csmKeys.ROSTERING_PERSON,
        helpContext: "person/browser"
      },
      {
        path: PERSON_ROUTES.ROLES,
        name: text("routes.person.roles_browser"),
        isExact: true,
        showInMenu: true,
        isNewTab: false,
        element: <PersonRolesBrowser />,
        permission: csmKeys.ROSTERING_PERSON_ROLES_BROWSER,
        helpContext: "person/rolesBrowser"
      },
      {
        path: PERSON_ROUTES.FINDER,
        name: text("routes.person.person_finder"),
        isExact: true,
        showInMenu: true,
        element: <Finder />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_PERSON,
        helpContext: "find"
      },
      {
        path: PERSON_ROUTES.R_ROLES,
        name: text("routes.person.roles"),
        isExact: true,
        element: <PersonRosterRolesContainer />,
        permission: csmKeys.ROSTERING_PERSON,
        helpContext: "person/roles"
      }
    ]
  },
  {
    path: ADMIN_ROUTES.ROOT,
    name: text("routes.administration.root"),
    isExact: true,
    showInMenu: true,
    isNewTab: false,
    element: <Navigate to={ADMIN_ROUTES.RUMS} replace={true} />,
    permission: csmKeys.ROSTERING_RUMS_VIEW,
    subRoutes: [
      {
        path: ADMIN_ROUTES.RUMS,
        name: text("routes.administration.rums"),
        isExact: true,
        showInMenu: true,
        element: <RUMS />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_RUMS_VIEW,
        helpContext: "admin/rumsSetup"
      },
      {
        path: ADMIN_ROUTES.MAPPINGS,
        name: text("routes.administration.mappings"),
        isExact: true,
        showInMenu: true,
        element: <ApprovalMappings />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_APPROVAL_VIEW,
        helpContext: "admin/approvalMappings"
      },
      {
        path: ADMIN_ROUTES.PERSON_ROLE_SETUP,
        name: text("routes.administration.person_role_setup"),
        isExact: true,
        showInMenu: true,
        element: <PersonRoleSetup />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_ROLES_SETUP_VIEW,
        helpContext: "admin/personRoleSetup"
      },
      {
        path: ADMIN_ROUTES.ATTRIBUTE_SETUP,
        name: text("routes.administration.attribute_setup"),
        isExact: true,
        showInMenu: true,
        element: <AttributeSetup />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_ATTRIBUTE_SETUP_VIEW,
        helpContext: "admin/attributeSetup"
      },
      {
        path: ADMIN_ROUTES.MANAGE_COMMITTEES,
        name: text("routes.administration.manage_committees"),
        isExact: true,
        showInMenu: true,
        element: <ManageCommittees />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_COMMITTEES_SETUP_VIEW,
        helpContext: "admin/manageCommittees"
      },
      {
        path: ADMIN_ROUTES.NCI_LAPS_GRANT,
        name: text("routes.administration.nci_laps_grant"),
        isExact: true,
        showInMenu: true,
        element: <NCILapsGrant />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_LAPS_GRANTS_ADMIN_VIEW,
        helpContext: "admin/lapsGrantAdministration"
      },
      {
        path: ADMIN_ROUTES.UNREGISTERED_INSTITUTIONS,
        name: text("routes.administration.unregistered_institutions"),
        isExact: true,
        showInMenu: true,
        element: <UnregisteredInstitution />,
        isNewTab: false,
        permission: csmKeys.ROSTERING_UNREGISTERED_SITES_EDIT,
        helpContext: "admin/unregisteredInstitutions"
      }
    ]
  }
];
export default appRoutes;
